import React, { useState, useEffect, useContext, useRef } from 'react';
import { Grid } from '@material-ui/core';
import Select from '../../../../components/forms/Select';
import CalendarTodayOutlined from '@material-ui/icons/CalendarTodayOutlined';
import Button from '../../../../components/common/Button';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useHistory, useLocation } from 'react-router-dom';
import TextField from '@material-ui/core/TextField';
import moment from 'moment';
import UsersAPI from '../../user-api';
import PillarAPI from '../../../PillarContainer/pillar-api';
import Alert from '../../../../components/common/Alert';
import Skeleton from 'react-placeholder';
import { UserContext } from '../userContext';
import { Input } from '../../../../components/forms';
import ControlPointSharpIcon from '@material-ui/icons/ControlPointSharp';
import { AppContext } from '../../../../app/AppContext';
import MeetingSummaryModal from '../../../../components/common/MeetingSummaryModal';

const getDisplayName = (person) => {
  if (!person) return '';
  if (person.name) return person.name;

  const fullName = [
    person.firstName,
    person.lastName || person.lastNames || person.surname,
  ]
    .filter(Boolean)
    .join(' ');

  return fullName || person.email || '';
};

const SetSessions = () => {
  const history = useHistory();
  const location = useLocation();
  const { clientId } = location.state;
  const time = moment().add(1, 'hour').format('YYYY-MM-DDTHH:mm');

  const [state, dispatch] = useContext(UserContext);
  const { user: authenticatedUser } = useContext(AppContext);
  const { user, mentors, futureSessions } = state;
  const isAdmin = authenticatedUser?.role?.type === 'admin';

  //set mentor options
  const mentorList = mentors.map((mentor) => ({
    id: mentor.id,
    value: mentor.name,
  }));

  const veces = Array.from({ length: 16 }, (x, i) => {
    if (i === 0) {
      return { value: `${i + 1} vez`, id: i + 1 };
    } else {
      return { value: `${i + 1} veces`, id: i + 1 };
    }
  });

  const dias = Array.from({ length: 15 }, (x, i) => {
    if (i === 0) {
      return { value: `${i + 1} dia`, id: i + 1 };
    } else {
      return { value: `${i + 1} dias`, id: i + 1 };
    }
  });
  const [message, setMessage] = useState({});
  const [messageDates, setMessageDates] = useState([]);
  const [initialHour, setInitialHour] = useState(null);
  const [initialDate, setInitialDate] = useState(null);
  const [initialTime, setInitialTime] = useState(time);
  const [inputVeces, setInputVeces] = useState(0);
  const [inputDias, setInputDias] = useState(0);
  const [mentor, setMentor] = useState(0);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [allPillars, setAllPillars] = useState([]);
  const [pillarId, setPillarId] = useState('');
  const [calculated, setCalculated] = useState(false);
  const [schedulesChecked, setSchedulesChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [guestEmails, setGuestEmails] = useState([]);
  const [alertMessage, setAlertMessage] = useState({ type: '', message: '' });
  const [meetingSummary, setMeetingSummary] = useState(null);
  const submissionInFlight = useRef(false);
  const scheduleCheckSequence = useRef(0);

  useEffect(() => {
    PillarAPI.fetchActivePillars().then((response) => {
      setAllPillars(response.data || []);
    });
  }, []);

  const selectedMentor = mentors.find(
    (mentorItem) => String(mentorItem.id) === String(mentor)
  );
  const mentorPillarOptions =
    selectedMentor && selectedMentor.prePillars
      ? allPillars
          .filter((pillar) =>
            selectedMentor.prePillars
              .split(',')
              .map((pillarEntry) => parseInt(pillarEntry, 10))
              .filter(Boolean)
              .includes(pillar.id)
          )
          .map((pillar) => ({ id: pillar.id, value: pillar.name }))
      : [];

  //schedule resume
  const initialSchedules = async () => {
    const checkSequence = ++scheduleCheckSequence.current;
    setMessageDates([]);
    setSchedulesChecked(false);
    setCalculated(true);
    if (validation()) {
      const schedulesArray = Array.from({ length: inputVeces }, (x, i) => {
        if (!i) {
          return moment(initialTime).format('YYYY-MM-DD HH:mm');
        } else {
          return moment(initialTime)
            .add(parseInt(inputDias * i), 'days')
            .format('YYYY-MM-DD HH:mm');
        }
      });
      dispatch({
        type: 'UPDATE_FUTURE_SESSIONS',
        payload: {
          futureSessions: {
            schedules: schedulesArray,
            userId: user.id,
            mentorId: mentor,
            subject,
            description,
            pillarId,
            notify: true,
            guestEmails,
          },
        },
      });

      try {
        const response = await UsersAPI.getBusySchedule(mentor, user.id);
        if (checkSequence !== scheduleCheckSequence.current) return;

        const busyDates = response.data || [];
        const scheduleMessages = schedulesArray.map((schedule) => {
          const busyDate = busyDates.find(
            (date) =>
              moment(schedule).format('YYYY-MM-DD HH:mm') ===
              moment(date.date).format('YYYY-MM-DD HH:mm')
          );

          return busyDate ? busyDate.message : undefined;
        });

        setMessageDates(scheduleMessages);
        setSchedulesChecked(true);
      } catch (error) {
        if (checkSequence !== scheduleCheckSequence.current) return;

        setAlertMessage({
          type: 'error',
          message:
            error?.data?.message ||
            'No fue posible verificar la disponibilidad de los participantes.',
        });
      } finally {
        if (checkSequence === scheduleCheckSequence.current) {
          setCalculated(false);
        }
      }
    }
  };

  const validation = () => {
    setMessage({});
    const actualDate = moment().subtract(1, 'hour');
    let validate = true;
    let validateMessage = {};
    if (
      !isAdmin &&
      initialTime &&
      !moment(initialTime).isAfter(actualDate.format('YYYY-MM-DDTHH:mm'))
    ) {
      validate = false;
      validateMessage.initialTime =
        'La fecha seleccionada no puede ser anterior a la fecha actual';
      setMessage(validateMessage);
    }
    if (!initialHour) {
      validate = false;
      validateMessage.initialHour = 'Ingrese Hora';
      setMessage(validateMessage);
    }
    if (!initialDate) {
      validate = false;
      validateMessage.initialDate = 'Ingrese Fecha';
      setMessage(validateMessage);
    }
    if (mentor === 0) {
      validate = false;
      validateMessage.mentor = 'Debe seleccionar un mentor';
      setMessage(validateMessage);
    }
    if (!pillarId) {
      validate = false;
      validateMessage.pillarId = 'Debe seleccionar un pilar';
      setMessage(validateMessage);
    }
    if (inputVeces === 0) {
      validate = false;
      validateMessage.inputVeces = 'Valor Requerido';
      setMessage(validateMessage);
    }
    if (inputDias === 0) {
      validate = false;
      validateMessage.inputDias = 'Valor Requerido';
      setMessage(validateMessage);
    }
    if (!subject.trim()) {
      validate = false;
      validateMessage.subject = 'Debe ingresar asunto';
      setMessage(validateMessage);
    }
    if (!description.trim()) {
      validate = false;
      validateMessage.description = 'Ingrese una pequeña descripción';
      setMessage(validateMessage);
    }
    if (!validate) {
      setCalculated(false);
    }
    return validate;
  };

  const handleOpenSummary = () => {
    if (calculated || submissionInFlight.current) return;

    const currentSchedules = Array.isArray(futureSessions.schedules)
      ? futureSessions.schedules
      : [];
    const hasScheduleErrors = messageDates.some(Boolean);

    if (!validation()) return;

    if (!currentSchedules.length || !schedulesChecked) {
      setAlertMessage({
        type: 'error',
        message:
          'Debes generar y verificar los horarios antes de confirmar las reuniones.',
      });
      return;
    }

    if (!isAdmin && hasScheduleErrors) return;

    const normalizedSchedules = currentSchedules.map((schedule) =>
      moment(
        schedule,
        ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD HH:mm'],
        true
      )
    );
    const scheduleKeys = normalizedSchedules.map((schedule) =>
      schedule.format('YYYY-MM-DD HH:mm')
    );
    const schedulesAreValid = normalizedSchedules.every((schedule) =>
      schedule.isValid()
    );
    const hasDuplicateSchedules =
      new Set(scheduleKeys).size !== scheduleKeys.length;

    if (
      !schedulesAreValid ||
      hasDuplicateSchedules ||
      currentSchedules.length !== Number(inputVeces)
    ) {
      setAlertMessage({
        type: 'error',
        message:
          'Revisa las fechas programadas: todas deben ser válidas y diferentes.',
      });
      return;
    }

    const selectedPillar = allPillars.find(
      (pillar) => String(pillar.id) === String(pillarId)
    );
    const mentorName = getDisplayName(selectedMentor);
    const participantName = getDisplayName(user);

    if (!mentorName || !participantName || !selectedPillar?.name) {
      setAlertMessage({
        type: 'error',
        message:
          'No fue posible obtener toda la información necesaria para confirmar.',
      });
      return;
    }

    const schedulesSnapshot = normalizedSchedules.map((schedule) =>
      schedule.format('YYYY-MM-DD HH:mm')
    );
    const totalSessions = schedulesSnapshot.length;
    const frequency =
      totalSessions === 1
        ? 'No se repite'
        : `Cada ${inputDias} ${Number(inputDias) === 1 ? 'día' : 'días'}`;

    setAlertMessage({ type: '', message: '' });
    setMeetingSummary({
      mentor: mentorName,
      participant: participantName,
      pillar: selectedPillar.name,
      schedules: schedulesSnapshot,
      frequency,
      totalSessions,
      subject,
      description,
      guestEmails: [...guestEmails],
      payload: {
        calendar: schedulesSnapshot,
        idMentee: user.id,
        idMentor: mentor,
        pillar: pillarId,
        title: subject,
        description,
        notify: true,
        clientId,
        guestEmails: [...guestEmails],
      },
    });
  };

  const handleSubmit = async () => {
    if (submissionInFlight.current || !meetingSummary) return;

    submissionInFlight.current = true;
    setIsSubmitting(true);

    try {
      const response = await UsersAPI.createMassiveSessions(
        meetingSummary.payload
      );

      dispatch({
        type: 'UPDATE_FUTURE_SESSIONS',
        payload: {
          futureSessions: {},
        },
      });
      setSchedulesChecked(false);
      setAlertMessage({ type: 'success', message: response.data.message });
      setMeetingSummary(null);
    } catch (error) {
      setAlertMessage({
        type: 'error',
        message:
          error?.data?.message || 'No fue posible agendar las reuniones.',
      });
      setSchedulesChecked(false);
      setMeetingSummary(null);
    } finally {
      submissionInFlight.current = false;
      setIsSubmitting(false);
    }
  };

  const invalidateSchedules = () => {
    scheduleCheckSequence.current += 1;
    setCalculated(false);
    setSchedulesChecked(false);
    setMessageDates([]);

    if (futureSessions.schedules?.length) {
      dispatch({
        type: 'UPDATE_FUTURE_SESSIONS',
        payload: {
          futureSessions: {},
        },
      });
    }
  };

  const changeInitialTime = (date, time) => {
    invalidateSchedules();
    let oldTime = initialTime.split(' ')[1];
    if (!time) {
      if (oldTime) {
        setInitialTime(date + ' ' + oldTime);
      } else {
        setInitialTime(date + ' 00:00');
      }
      setInitialDate(date);
    } else {
      setInitialHour(time);
      setInitialTime(moment(initialTime).format('YYYY-MM-DD') + ' ' + time);
    }
  };
  const addGuest = (values) => {
    const email = values.guestEmail?.trim();
    const isValidEmail = Yup.string().email().required().isValidSync(email);

    if (!isValidEmail) {
      setAlertMessage({
        type: 'error',
        message: 'Ingresa un correo electrónico válido.',
      });
      return;
    }

    if (guestEmails.length >= 4) {
      setAlertMessage({
        type: 'error',
        message: 'Se ha alcanzado el límite máximo de correos electrónicos.',
      });
      return;
    }

    if (
      guestEmails.some(
        (guestEmail) => guestEmail.toLowerCase() === email.toLowerCase()
      )
    ) {
      setAlertMessage({
        type: 'error',
        message: 'El correo electrónico ya fue agregado.',
      });
      return;
    }

    setAlertMessage({ type: '', message: '' });
    setGuestEmails([...guestEmails, email]);
  };
  const removeGuestEmail = (index) => {
    const updatedGuestEmails = [...guestEmails];
    updatedGuestEmails.splice(index, 1);
    setGuestEmails(updatedGuestEmails);
  };
  const changeIndividualTime = async (key, date, time) => {
    const schedulesArray = [...(futureSessions.schedules || [])];
    if (!schedulesArray[key]) return;

    const checkSequence = ++scheduleCheckSequence.current;
    setSchedulesChecked(false);
    const actualDate = moment().subtract(1, 'hour');
    const oldDate = schedulesArray[key].split(' ')[0];
    const oldTime = schedulesArray[key].split(' ')[1];
    if (date) {
      schedulesArray[key] = moment(date).format('YYYY-MM-DD') + ' ' + oldTime;
    }
    if (time) {
      schedulesArray[key] = moment(oldDate).format('YYYY-MM-DD') + ' ' + time;
    }

    dispatch({
      type: 'UPDATE_FUTURE_SESSIONS',
      payload: {
        futureSessions: {
          schedules: schedulesArray,
          userId: user.id,
          mentorId: mentor,
          pillarId,
          subject,
          description,
          notify: true,
          guestEmails,
        },
      },
    });

    try {
      const response = await UsersAPI.getBusySchedule(mentor, user.id);
      if (checkSequence !== scheduleCheckSequence.current) return;

      const busyDates = response.data || [];
      const scheduleMessages = schedulesArray.map((schedule) => {
        if (
          !moment(schedule).isAfter(actualDate.format('YYYY-MM-DDTHH:mm'))
        ) {
          return 'La fecha ingresada no puede ser menor a la actual.';
        }

        const busyDate = busyDates.find(
          (dateItem) =>
            moment(schedule).format('YYYY-MM-DD HH:mm') ===
            moment(dateItem.date).format('YYYY-MM-DD HH:mm')
        );

        return busyDate ? busyDate.message : undefined;
      });

      setMessageDates(scheduleMessages);
      setSchedulesChecked(true);
    } catch (error) {
      if (checkSequence !== scheduleCheckSequence.current) return;

      setAlertMessage({
        type: 'error',
        message:
          error?.data?.message ||
          'No fue posible verificar la disponibilidad de los participantes.',
      });
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={{
        mentorId: mentor,
        pillarId,
        dias: inputDias,
        veces: inputVeces,
      }}
      validationSchema={Yup.object({
        mentorId: Yup.string().required(
          'Seleccione mentor para asignar reuniones'
        ),
        guestEmail: Yup.string()
          .trim()
          .email('Ingrese un correo electrónico válido'),
      })}
      onSubmit={() => {}}
    >
      {({ values }) => (
        <Form>
          <MeetingSummaryModal
            open={Boolean(meetingSummary)}
            onCancel={() => {
              if (!submissionInFlight.current) setMeetingSummary(null);
            }}
            onAccept={handleSubmit}
            isSubmitting={isSubmitting}
            mentor={meetingSummary?.mentor}
            participant={meetingSummary?.participant}
            pillar={meetingSummary?.pillar}
            schedules={meetingSummary?.schedules || []}
            frequency={meetingSummary?.frequency}
            totalSessions={meetingSummary?.totalSessions}
            subject={meetingSummary?.subject}
            description={meetingSummary?.description}
            guestEmails={meetingSummary?.guestEmails || []}
          />
          {mentorList.length === 0 ? (
            <Grid container>
              <Grid item xs={12}>
                <Skeleton showLoadingAnimation type="text" rows={15} />
              </Grid>
            </Grid>
          ) : (
            <>
              <Grid container spacing={4} className="future-sessions">
                <Grid item xs={12}>
                  {alertMessage.message && (
                    <Alert type={alertMessage.type}>
                      {alertMessage.message}
                    </Alert>
                  )}
                  <h3>Agendar reuniones futuras</h3>
                </Grid>
                <Grid item className="future-sessions-column" xs={12} sm={5}>
                  <h4>Mentor</h4>
                  <Select
                    name="mentorId"
                    label="Nombre Mentor"
                    type="select"
                    options={[]}
                    advancedOptions={mentorList}
                    placeholder="Nombre Mentor"
                    onChange={(event) => {
                      invalidateSchedules();
                      setMentor(event.target.value);
                      setPillarId('');
                    }}
                  />
                  {message.mentor && (
                    <div className="error-message">{message.mentor}</div>
                  )}
                  <h4>Pilar del mentor</h4>
                  <Select
                    name="pillarId"
                    label="Pilar"
                    type="select"
                    options={[]}
                    advancedOptions={mentorPillarOptions}
                    placeholder="Selecciona un pilar"
                    disabled={!mentor || mentorPillarOptions.length === 0}
                    onChange={(event) => {
                      setPillarId(event.target.value);
                    }}
                  />
                  {message.pillarId && (
                    <div className="error-message">{message.pillarId}</div>
                  )}
                  <Grid item xs={12}>
                    <h4>Seleccionar día y hora inicial</h4>
                  </Grid>
                  <Grid container>
                    <Grid item xs={6} className="date-grid">
                      <TextField
                        className="text-input"
                        id="date"
                        type="date"
                        label="  "
                        defaultValue={initialTime}
                        onChange={(event) => {
                          changeInitialTime(
                            moment(event.target.value).format('YYYY-MM-DD'),
                            ''
                          );
                        }}
                      />
                      {message.initialDate && (
                        <div
                          className="error-message"
                          style={{ marginTop: '20px' }}
                        >
                          {message.initialDate}
                        </div>
                      )}
                    </Grid>
                    <Grid item xs={6} className="hour1-grid">
                      <Select
                        name="hora"
                        placeholder="Hora"
                        label="Hora"
                        type="select"
                        options={[]}
                        advancedOptions={[
                          { id: 8, value: '08:00' },
                          { id: 9, value: '09:00' },
                          { id: 10, value: '10:00' },
                          { id: 11, value: '11:00' },
                          { id: 12, value: '12:00' },
                          { id: 13, value: '13:00' },
                          { id: 14, value: '14:00' },
                          { id: 15, value: '15:00' },
                          { id: 16, value: '16:00' },
                          { id: 17, value: '17:00' },
                          { id: 18, value: '18:00' },
                          { id: 19, value: '19:00' },
                          { id: 20, value: '20:00' },
                          { id: 21, value: '21:00' },
                        ]}
                        onChange={(event) => {
                          changeInitialTime('', event.target.value + ':00');
                        }}
                      />
                      {message.initialHour && (
                        <div className="error-message">
                          {message.initialHour}
                        </div>
                      )}
                    </Grid>
                    {message.initialTime && (
                      <Grid item xs={12}>
                        <div className="error-message">
                          {message.initialTime}
                        </div>
                      </Grid>
                    )}
                  </Grid>
                  <h4>Repetir</h4>
                  <Grid container>
                    <Grid item xs={5}>
                      <Select
                        name="veces"
                        label=""
                        type="select"
                        options={[]}
                        advancedOptions={veces}
                        placeholder="Nombre Mentor"
                        onChange={(event) => {
                          invalidateSchedules();
                          setInputVeces(event.target.value);
                        }}
                      />
                      {message.inputVeces && (
                        <div className="error-message">
                          {message.inputVeces}
                        </div>
                      )}
                    </Grid>
                    <Grid item xs={2} style={{ textAlign: 'center' }}>
                      <h4 style={{ marginTop: '12px' }}>Cada</h4>
                    </Grid>
                    <Grid item xs={5}>
                      <Select
                        name="dias"
                        label=""
                        type="select"
                        options={[]}
                        advancedOptions={dias}
                        placeholder="Nombre Mentor"
                        onChange={(event) => {
                          invalidateSchedules();
                          setInputDias(event.target.value);
                        }}
                      />
                      {message.inputDias && (
                        <div className="error-message">{message.inputDias}</div>
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <h4>Asunto</h4>
                      <TextField
                        className="text-input"
                        name="subject"
                        type="text"
                        value={subject}
                        onChange={(event) => {
                          setSubject(event.target.value);
                        }}
                      />
                      {message.subject && (
                        <div className="error-message">{message.subject}</div>
                      )}
                    </Grid>
                    <Grid item xs={12} className="description-box">
                      <h4>Descripción</h4>
                      <TextField
                        rows={4}
                        multiline
                        className="text-input"
                        name="description"
                        label="Ingrese descripción"
                        type="textarea"
                        value={description}
                        onChange={(event) => {
                          setDescription(event.target.value);
                        }}
                      />
                      {message.description && (
                        <div className="error-message">
                          {message.description}
                        </div>
                      )}
                    </Grid>
                    <Grid item xs={12} className="description-box">
                      <h4>Agregar invitado a mentoría</h4>
                      <p style={{ color: '#11111180', padding: '10px' }}>
                        Ingresa el correo del invitado para notificar <br /> su
                        invitación a la mentoría
                      </p>
                      <Input
                        label="usuario@mail.com"
                        name="guestEmail"
                        type="email"
                      />

                      <div
                        style={{
                          margin: '10px 0',
                          color: '#38bdf8',
                          display: 'flex',
                          cursor: 'pointer',
                        }}
                        onClick={() => addGuest(values, guestEmails)}
                      >
                        <ControlPointSharpIcon />
                        Agregar invitado
                      </div>
                      <ul>
                        {guestEmails.map((email, index) => (
                          <div className="modal__header" key={index}>
                            {email}
                            <span
                              className="material-icons icon"
                              onClick={() => removeGuestEmail(index)}
                            >
                              close
                            </span>
                          </div>
                        ))}
                      </ul>
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        onClick={() => initialSchedules()}
                        primary
                        widthFull
                        loading={calculated}
                      >
                        Ver Horarios
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item className="future-sessions-column" xs={12} sm={7}>
                  <div className="future-sessions-subtitle">
                    <h4 className="subtitle">Fecha y hora programada</h4>
                  </div>
                  {/* future sessions summary */}
                  {!calculated && futureSessions.schedules?.length ? (
                    <Grid className="shedules-box">
                      {futureSessions &&
                        futureSessions?.schedules?.map((schedule, key) => {
                          return (
                            <Grid container key={key}>
                              <Grid item xs={2} className="name-grid">
                                <h4>Sesión {key + 1}:</h4>
                              </Grid>
                              <Grid item xs={6} className="date-grid">
                                <TextField
                                  className="text-input"
                                  id="date"
                                  type="date"
                                  label=""
                                  defaultValue={schedule.split(' ')[0]}
                                  onChange={(event) => {
                                    changeIndividualTime(
                                      key,
                                      event.target.value,
                                      null
                                    );
                                  }}
                                />
                                {messageDates[key] && (
                                  <div className="error-message error-grid">
                                    {messageDates[key]}
                                  </div>
                                )}
                              </Grid>
                              <Grid item xs={3} className="hour-grid">
                                <Select
                                  name="hora"
                                  placeholder="Hora"
                                  label="Hora"
                                  type="select"
                                  defaultValue={schedule.split(' ')[1]}
                                  options={[]}
                                  onChange={(event) => {
                                    changeIndividualTime(
                                      key,
                                      null,
                                      event.target.value
                                    );
                                  }}
                                  advancedOptions={[
                                    { id: '08:00', value: '08:00' },
                                    { id: '09:00', value: '09:00' },
                                    { id: '10:00', value: '10:00' },
                                    { id: '11:00', value: '11:00' },
                                    { id: '12:00', value: '12:00' },
                                    { id: '13:00', value: '13:00' },
                                    { id: '14:00', value: '14:00' },
                                    { id: '15:00', value: '15:00' },
                                    { id: '16:00', value: '16:00' },
                                    { id: '17:00', value: '17:00' },
                                    { id: '18:00', value: '18:00' },
                                    { id: '19:00', value: '19:00' },
                                    { id: '20:00', value: '20:00' },
                                    { id: '21:00', value: '21:00' },
                                  ]}
                                />
                              </Grid>
                              <Grid item xs={1}></Grid>
                            </Grid>
                          );
                        })}
                    </Grid>
                  ) : (
                    <div className="empty-box">
                      <CalendarTodayOutlined
                        style={{ fontSize: 120 }}
                        color="disabled"
                      />
                    </div>
                  )}
                </Grid>
              </Grid>
              <Grid container style={{ marginTop: '20px' }}>
                <Grid item xs={12}>
                  <Grid
                    container
                    justify="center"
                    alignItems="center"
                    spacing={3}
                  >
                    <Grid item xs={4}>
                      <Button
                        onClick={() => history.push('/usuarios')}
                        secondary
                        widthFull
                      >
                        Cancelar
                      </Button>
                    </Grid>
                    <Grid item xs={4}>
                      <Button
                        onClick={handleOpenSummary}
                        primary
                        widthFull
                        disabled={calculated || isSubmitting}
                      >
                        Confirmar Reuniones
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </>
          )}
        </Form>
      )}
    </Formik>
  );
};

export default SetSessions;
